'use client';
import React, { useEffect, useState } from 'react';
import { ChevronDown, Lock, PlayCircle, BookOpenCheck, ListChecks, KeyRound, CheckCircle2 } from 'lucide-react';
import SignupModal from './SignupModal';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';


const ALLOWED_SOURCES = ['instagram', 'tiktok', 'facebook', 'linkedin', 'whatsapp'];

const WHO_FOR = [
'You are thinking about starting a domiciliary care or supported living business',
'You have care experience but have never run a business',
'You want to understand what CQC registration and running a regulated service really involve before you commit',
'You want an honest starting point, not a sales pitch'];


const MODULES = [
'Why People Start Care Businesses',
'What Running a Regulated Service Really Involves',
'Your Skills Audit: Care Experience, Management, Finance and Leadership',
'Your Values and the Kind of Provider You Want to Be',
'Making the Decision: Time, Money and Family Readiness',
'Next Steps: your options for continuing your journey'];


const OUTCOMES = [
'Explain the main legal and personal responsibilities of owning a CQC-registered service',
'Complete a skills audit and name the gaps you need to fill',
'Write a vision statement and three to five core values',
'Make an informed decision about whether to go ahead'];


const INCLUDED = [
{ icon: PlayCircle, text: 'Short video lessons from Linda' },
{ icon: BookOpenCheck, text: 'A built-in workbook to record your answers and reflections as you go' },
{ icon: ListChecks, text: 'A short quiz to check your understanding' },
{ icon: KeyRound, text: 'Your own private Care Business School portal login' }];


const FAQS = [
{
  q: 'Do I need care experience?',
  a: 'No. The course helps you see what skills you have and which gaps you need to fill, whether you have care experience or not.'
},
{
  q: 'How long does it take?',
  a: 'About 1 hour 30 minutes in total. You can go at your own pace and come back to it any time.'
},
{
  q: 'What happens after I pay?',
  a: "You'll get an email invitation to set up your login to the Care Business School portal. Please check your spam or junk folder if it doesn't arrive within a few minutes. If you already have an account, just log in."
},
{
  q: 'What comes after Course 1?',
  a: 'At the end of the course you can choose to continue with the Growers Membership or the Growers & Governance Membership. There is no obligation to continue.'
},
{
  q: 'Does this course register my business with CQC?',
  a: 'No. It helps you decide whether a care business is right for you and what is involved, before you commit time and money.'
},
{
  q: 'I have a question before I buy.',
  a: 'Email hello@carebusinessschool.co.uk.'
}];


export default function StartLanding() {
  const [source, setSource] = useState('start-page');
  const [open, setOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    const param = new URLSearchParams(window.location.search)?.get('source')?.trim()?.toLowerCase();
    if (param && ALLOWED_SOURCES?.includes(param)) setSource(param);
  }, []);

  const ctaClass =
  'inline-flex min-h-[56px] w-full items-center justify-center rounded-xl bg-[#C6A667] px-8 py-4 text-base font-bold text-[#2B0D18] shadow-lg transition-all duration-150 hover:brightness-105 active:scale-95 sm:w-auto sm:text-lg';

  return (
    <div>
      {/* Section 1: Hero */}
      <section className="bg-[#581C32] text-white">
        <div className="mx-auto grid w-full max-w-screen-xl items-center gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-2 lg:gap-14 lg:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C6A667]">Course 1 · Start here</p>
            <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              Thinking about starting a care business? Start here.
            </h1>
            <p className="mt-5 text-base leading-relaxed text-white/85 sm:text-lg">
              Before you spend money on registration, offices or staff, find out if owning a care business is really right
              for you. Course 1 helps you look honestly at your reasons, your skills and what the role will ask of you and
              your family.
            </p>
            <p className="mt-6 text-lg font-bold text-[#C6A667]">£49 · one payment · start straight away</p>
            <div className="mt-5">
              <button type="button" onClick={() => setOpen(true)} className={ctaClass}>
                Start Course 1 for £49
              </button>
            </div>
            <p className="mt-3 text-sm text-white/70">About 1 hour 30 minutes · Learn at your own pace · Online</p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-[#C6A667]/40 shadow-2xl">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1ecb066cf-1773707599567.png"
              alt="Care worker sitting beside an older woman at home, smiling and talking together"
              width={800}
              height={450}
              loading="lazy"
              decoding="async"
              className="h-auto w-full object-cover" />
            
          </div>
        </div>
      </section>

      {/* Section 2: Who it's for */}
      <section className="bg-[#F8F3E9]">
        <div className="mx-auto w-full max-w-screen-lg px-4 py-14 md:px-6 md:py-20">
          <h2 className="text-2xl font-extrabold tracking-tight text-[#581C32] sm:text-3xl">This course is for you if…</h2>
          <ul className="mt-8 space-y-4">
            {WHO_FOR?.map((item) =>
            <li key={item} className="flex items-start gap-3 rounded-xl bg-white p-4 shadow-sm">
                <CheckCircle2 size={22} className="mt-0.5 flex-shrink-0 text-[#C6A667]" />
                <span className="text-base text-[#2B0D18]">{item}</span>
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* Section 3: What you'll learn */}
      <section className="bg-white">
        <div className="mx-auto w-full max-w-screen-xl px-4 py-14 md:px-6 md:py-20 lg:px-8">
          <h2 className="text-2xl font-extrabold tracking-tight text-[#581C32] sm:text-3xl">
            What&apos;s inside Course 1: Is a Care Business Right for You?
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {MODULES?.map((title, i) =>
            <div key={title} className="rounded-xl border border-[#C6A667]/50 bg-[#F8F3E9] p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#581C32] text-base font-bold text-[#C6A667]">
                  {i + 1}
                </span>
                <h3 className="mt-3 text-base font-bold leading-snug text-[#581C32]">{title}</h3>
              </div>
            )}
          </div>
          <h3 className="mt-12 text-xl font-bold text-[#581C32]">By the end you will be able to:</h3>
          <ul className="mt-5 space-y-3">
            {OUTCOMES?.map((o) =>
            <li key={o} className="flex items-start gap-3 text-base text-[#2B0D18]">
                <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0 text-[#C6A667]" />
                <span>{o}</span>
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* Section 4: What you get */}
      <section className="bg-[#F8F3E9]">
        <div className="mx-auto w-full max-w-screen-xl px-4 py-14 md:px-6 md:py-20 lg:px-8">
          <h2 className="text-2xl font-extrabold tracking-tight text-[#581C32] sm:text-3xl">What you get</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {INCLUDED?.map(({ icon: Icon, text }) =>
            <div key={text} className="rounded-xl bg-white p-6 shadow-sm">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#581C32] text-[#C6A667]">
                  <Icon size={24} />
                </span>
                <p className="mt-4 text-base font-semibold text-[#2B0D18]">{text}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Section 5: About Linda */}
      <section className="bg-white">
        <div className="mx-auto grid w-full max-w-screen-xl items-center gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-[360px_1fr] lg:gap-14 lg:px-8">
          <div className="relative mx-auto aspect-square w-full max-w-sm overflow-hidden rounded-2xl border-2 border-[#C6A667] bg-[#F8F3E9]">
            <Image
              src="/assets/images/CBS_Picture-1793217669093.jpeg"
              alt="Linda Dube, Care Business Coach and founder of Care Business School"
              fill
              sizes="(max-width: 1024px) 100vw, 360px"
              className="object-cover" />
          </div>
          <div>
            <h2 className="text-2xl font-normal tracking-tight text-[#581C32] sm:text-3xl">Meet Your Coach: Linda Dube</h2>
            <p className="mt-2 text-sm font-semibold uppercase tracking-wide text-[#C6A667]">
              Care Business Coach · Social Care Entrepreneur · Registered Social Worker and Best Interests Assessor · Trainer
            </p>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-[#2B0D18]/85 sm:text-lg">
              <p>
                Linda Dube is a Care Business Coach, Social Care Entrepreneur, Registered Social Worker and Best
                Interests Assessor, and Trainer, with over 20 years of experience in adult social care. In 2023 she
                founded TLS Social Care Ltd, a CQC-registered domiciliary care and supported living provider in Leeds,
                and grew it to a six-figure turnover and more than 40 staff. She created Care Business School to give
                aspiring and existing care business owners the honest, practical guidance she wishes she had when she
                started.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Questions */}
      <section className="bg-[#F8F3E9]">
        <div className="mx-auto w-full max-w-screen-md px-4 py-14 md:px-6 md:py-20">
          <h2 className="text-2xl font-extrabold tracking-tight text-[#581C32] sm:text-3xl">Questions</h2>
          <div className="mt-8 space-y-3">
            {FAQS?.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={f?.q} className="overflow-hidden rounded-xl border border-[#C6A667]/50 bg-white">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="flex min-h-[56px] w-full items-center justify-between gap-4 px-5 py-4 text-left text-base font-bold text-[#581C32]">
                    
                    <span>{f?.q}</span>
                    <ChevronDown size={20} className={`flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && <p className="px-5 pb-5 text-base leading-relaxed text-[#2B0D18]/85">{f?.a}</p>}
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* Final call to action */}
      <section className="bg-[#581C32] text-white">
        <div className="mx-auto flex w-full max-w-screen-md flex-col items-center px-4 py-14 text-center md:py-16">
          <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Ready to find out if it&apos;s right for you?</h2>
          <p className="mt-3 flex items-center gap-2 text-base text-[#C6A667]">
            <Lock size={16} /> £49 · one payment · start straight away
          </p>
          <div className="mt-6 w-full sm:w-auto">
            <button type="button" onClick={() => setOpen(true)} className={ctaClass}>
              Start Course 1 for £49
            </button>
          </div>
        </div>
      </section>

      <SignupModal open={open} onClose={() => setOpen(false)} source={source} />
    </div>);

}